/**
 * The decisions behind the floating "report a bug" camera button, with no Angular
 * in them (fifth slice of deferred finding df-7b66acbeb341).
 *
 * They lived on `AppComponent`, which cannot be loaded under the node test
 * runner, so none of them could be tested. The Angular half —
 * `bug-report-fab.service.ts` — reads the page and pointer state and hands it in
 * here as plain values. Every rule below is the one the component applied.
 */

/** Pages the button never shows on: the feedback form it leads to, and sign-in. */
export const PAGES_WITHOUT_REPORT_BUG_FAB = ['SendFeedbackPage', 'LoginPage'];

/**
 * How far, in pixels on either axis, the pointer must travel from where it went
 * down before the gesture counts as a drag rather than a click.
 */
export const BUG_FAB_DRAG_THRESHOLD_PX = 5;

/** Whether the button shows: the user switched it on, and the page is not one of the excluded ones. */
export function shouldShowReportBugFab(activePageName: string | undefined, switchedOn: boolean): boolean {
  return !PAGES_WITHOUT_REPORT_BUG_FAB.some(page => page === activePageName) && switchedOn;
}

/** Whether the pointer has moved far enough from its starting point for the gesture to count as a drag. */
export function hasBugFabDragStarted(start: { x: number; y: number }, now: { x: number; y: number }): boolean {
  return Math.abs(now.x - start.x) > BUG_FAB_DRAG_THRESHOLD_PX ||
    Math.abs(now.y - start.y) > BUG_FAB_DRAG_THRESHOLD_PX;
}

/**
 * Where the button's top-left corner goes, kept so the whole button stays on
 * screen. `fabSize` is the button's width, used for both axes as before.
 *
 * `insets` are the status bar above and the system navigation bar below, in
 * pixels. The native apps draw under both, so a button dragged into either would
 * sit behind the system's own controls and could not be tapped (#5810).
 */
export function clampBugFabPosition(
  wanted: { x: number; y: number },
  fabSize: number,
  viewport: { width: number; height: number },
  insets: { top: number; bottom: number } = { top: 0, bottom: 0 },
): { x: number; y: number } {
  return {
    x: Math.max(0, Math.min(viewport.width - fabSize, wanted.x)),
    y: Math.max(insets.top, Math.min(viewport.height - insets.bottom - fabSize, wanted.y)),
  };
}
